import React, { useState, useEffect, useRef } from 'react';
import { categorieService } from '../../api';
import { useNavigate } from 'react-router-dom';
import './Categorie.css';
import { Link } from 'react-router-dom';
import { 
  FaCar, FaHome, FaTools, FaLaptop, FaGraduationCap, 
  FaTree, FaTshirt, FaUtensils, FaSearch, FaFilter,
  FaTimes, FaArrowRight, FaPlusCircle
} from 'react-icons/fa';
import { FiMapPin } from 'react-icons/fi';

const Categorie = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubService, setSelectedSubService] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState({
    categories: true,
    annonces: false,
    villes: false
  });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    ville: '',
    service: '', // Changé de 'theme' à 'service' pour correspondre à l'API
    profil: '',
    type: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [showVilleSuggestions, setShowVilleSuggestions] = useState(false);
  const navigate = useNavigate();
  
  // Références pour le défilement
  const annoncesSectionRef = useRef(null);
  const searchHeroRef = useRef(null);

  const categoryIcons = {
    'Automobile': <FaCar className="category-icon" />,
    'Immobilier': <FaHome className="category-icon" />,
    'Bricolage': <FaTools className="category-icon" />,
    'Informatique': <FaLaptop className="category-icon" />,
    'Enseignement': <FaGraduationCap className="category-icon" />,
    'Jardinage': <FaTree className="category-icon" />,
    'Mode': <FaTshirt className="category-icon" />,
    'Restauration': <FaUtensils className="category-icon" />,
  };

  const profilOptions = ['Expert', 'Étudiant', 'Mentor', 'Entreprise'];
  const typeOptions = ['offre', 'demande'];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categorieService.getAll();
        setCategories(data);
        setLoading(prev => ({ ...prev, categories: false }));
      } catch (err) {
        setError(err.message);
        setLoading(prev => ({ ...prev, categories: false }));
      }
    };

    fetchCategories();
  }, []);

  // Fonction pour construire l'URL de l'image
  const getImageUrl = (annonce) => {
    if (!annonce || !annonce.image) {
      return '/default-annonce.jpg';
    }
    
    if (annonce.image.startsWith('http')) {
      return annonce.image;
    }
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}/storage/${
      annonce.image.startsWith('storage/') 
        ? annonce.image.replace('storage/', '') 
        : annonce.image
    }`;
  };

  // Fonction pour faire défiler vers les résultats
  const scrollToResults = () => {
    if (annoncesSectionRef.current) {
      setTimeout(() => {
        annoncesSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // Fonction pour faire défiler vers le haut
  const scrollToTop = () => {
    if (searchHeroRef.current) {
      searchHeroRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Fonction pour rechercher les villes françaises
  const searchVilles = async (query) => {
    if (query.length < 2) {
      setVilleSuggestions([]);
      return;
    }

    setLoading(prev => ({ ...prev, villes: true }));
    try {
      const response = await fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom,code,codesPostaux,departement,region&limit=10`);
      const data = await response.json();
      setVilleSuggestions(data);
    } catch (err) {
      console.error('Erreur lors de la recherche des villes:', err);
    } finally {
      setLoading(prev => ({ ...prev, villes: false }));
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubService(null);
    setAnnonces([]);
    setSearchTerm('');
    setFilters(prev => ({ ...prev, service: '', categorie_id: category.id }));
    scrollToTop();
  };

  const handleSubServiceClick = async (subService) => {
    setSelectedSubService(subService);
    
    // Construire les paramètres de recherche
    const searchParams = {
      search: searchTerm,
      ville: filters.ville,
      service: subService.id, // Utiliser l'ID du sous-service
      profil: filters.profil,
      type: filters.type
    };
    
    try {
      setLoading(prev => ({ ...prev, annonces: true }));
      const data = await categorieService.advancedSearch(searchParams);
      
      // Ajouter les URLs d'images aux annonces
      const annoncesAvecImages = Array.isArray(data) ? data.map(annonce => ({
        ...annonce,
        imageUrl: getImageUrl(annonce)
      })) : [];
      
      setAnnonces(annoncesAvecImages);
      
      // Faire défiler vers les résultats
      setTimeout(() => {
        scrollToResults();
      }, 300);
      
    } catch (err) {
      setError(err.message || 'Erreur lors de la récupération des annonces');
    } finally {
      setLoading(prev => ({ ...prev, annonces: false }));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleVilleChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, ville: value }));
    searchVilles(value);
    setShowVilleSuggestions(true);
  };

  const selectVille = (ville) => {
    const villeFormattee = `${ville.nom} (${ville.codesPostaux[0]})`;
    setFilters(prev => ({ ...prev, ville: villeFormattee }));
    setShowVilleSuggestions(false);
  };

  const handleAnnonceClick = (annonceId) => {
    navigate(`/annonces/${annonceId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    if (searchTerm.trim() === '' && !selectedSubService) {
      scrollToTop();
      return;
    }
    
    // Mettre à jour le filtre de recherche
    setFilters(prev => ({ ...prev, search: searchTerm }));
    
    // Lancer la recherche
    performSearch();
  };

  const performSearch = async () => {
    try {
      setLoading(prev => ({ ...prev, annonces: true }));
      
      // Construire les paramètres de recherche
      const searchParams = {
        search: searchTerm,
        ville: filters.ville,
        service: filters.service,
        profil: filters.profil,
        type: filters.type
      };
      
      // Si une catégorie est sélectionnée, l'ajouter aux paramètres
      if (selectedCategory) {
        searchParams.categorie_id = selectedCategory.id;
      }
      
      const data = await categorieService.advancedSearch(searchParams);
      
      // Ajouter les URLs d'images aux annonces
      const annoncesAvecImages = Array.isArray(data) ? data.map(annonce => ({
        ...annonce,
        imageUrl: getImageUrl(annonce)
      })) : [];
      
      setAnnonces(annoncesAvecImages);
      
      // Faire défiler vers les résultats
      setTimeout(() => {
        scrollToResults();
      }, 300);
      
    } catch (err) {
      setError(err.message || 'Erreur lors de la recherche');
    } finally {
      setLoading(prev => ({ ...prev, annonces: false }));
    }
  };

  const handleFiltersSubmit = (e) => {
    e.preventDefault();
    performSearch();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      ville: '',
      service: '',
      profil: '',
      type: ''
    });
    setSearchTerm('');
    setVilleSuggestions([]);
    setSelectedSubService(null);
    setSelectedCategory(null);
    setAnnonces([]);
    scrollToTop();
  };

  const filteredCategories = categories.filter(category => 
    category.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.sous_services?.some(service => 
      service.nom.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading.categories) return <div className="loading">Chargement des catégories...</div>;
  if (error) return <div className="error">Erreur: {error}</div>;

  return (
    <div className="categories-container">
      {/* Barre de recherche principale avec ref */}
      <div className="search-hero" ref={searchHeroRef}>
        <h1 className='title'>Trouvez le service parfait</h1>
        <p>Des milliers de professionnels à votre service</p>
         
        <form onSubmit={handleSearchSubmit} className="main-search-form">
          <div className="search-input-container">
            <FaSearch className="" />
            <input
              type="text"
              placeholder="Rechercher un service, une compétence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="search-button">
              Rechercher
            </button>
          </div>
        </form>
      </div>

      {/* Filtres avancés */}
      <div className="filters-toggle" onClick={() => setShowFilters(!showFilters)}>
        <FaFilter /> Filtres avancés {showFilters ? <FaTimes /> : <FaArrowRight />}
      </div>

      {showFilters && (
        <div className="filters-sectionadvanded">
          <form onSubmit={handleFiltersSubmit} className="filters-form">
            <div className="filter-group">
              <label htmlFor="ville">Ville</label>
              <div className="ville-autocomplete">
                <input
                  type="text"
                  id="ville"
                  name="ville"
                  placeholder="Entrez une ville française"
                  value={filters.ville}
                  onChange={handleVilleChange}
                  onFocus={() => setShowVilleSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowVilleSuggestions(false), 200)}
                />
                {showVilleSuggestions && villeSuggestions.length > 0 && (
                  <div className="ville-suggestions">
                    {villeSuggestions.map(ville => (
                      <div 
                        key={ville.code} 
                        className="ville-suggestion"
                        onClick={() => selectVille(ville)}
                      >
                        {ville.nom} ({ville.codesPostaux[0]}) - {ville.departement.nom}, {ville.region.nom}
                      </div>
                    ))}
                  </div>
                )}
                {loading.villes && (
                  <div className="loading-villes">Chargement...</div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label htmlFor="service">Service</label>
              <select 
                id="service" 
                name="service"
                value={filters.service}
                onChange={handleFilterChange}
              >
                <option value="">Tous les services</option>
                {categories.map(category => (
                  <optgroup key={category.id} label={category.nom}>
                    {category.sous_services?.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.nom}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="profil">Profil</label>
              <select 
                id="profil" 
                name="profil"
                value={filters.profil}
                onChange={handleFilterChange}
              >
                <option value="">Tous les profils</option>
                {profilOptions.map(profil => (
                  <option key={profil} value={profil.toLowerCase()}>
                    {profil}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="type">Type</label>
              <select 
                id="type" 
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">Offre/Demande</option>
                {typeOptions.map(type => (
                  <option key={type} value={type}>
                    {type === 'offre' ? 'Offre' : 'Demande'}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-actions">
              <button type="submit" className="filter-submit">
                <FaSearch /> Filtrer
              </button>
              <button type="button" onClick={clearFilters} className="filter-clearadvanced">
                Effacer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Navigation par catégories */}
      <div className="hero-btn-container">
        <Link to="/ajouter-annonce" className="hero-btnhero">
          <FaPlusCircle className="icon" /> Déposer une annonce
        </Link>
      </div>
      
      <div className="categories-section">
        <h2>Parcourir par catégorie</h2>
        
        <div className="categories-horizontal-scroll">
          {filteredCategories.map((category) => (
            <div 
              key={category.id} 
              className={`category-card ${selectedCategory?.id === category.id ? 'selected' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              <div className="category-icon-wrapper">
                {categoryIcons[category.nom] || <FaTools className="category-icon" />}
              </div>
              <h3>{category.nom}</h3>
              <p>{category.sous_services?.length} services</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sous-services de la catégorie sélectionnée */}
      {selectedCategory && (
        <div className="subservices-section">
          <h3>Services dans {selectedCategory.nom}</h3>
          <div className="subservices-grid">
            {selectedCategory.sous_services?.map((service) => (
              <div 
                key={service.id} 
                className={`subservice-card ${selectedSubService?.id === service.id ? 'selected' : ''}`}
                onClick={() => handleSubServiceClick(service)}
              >
                <h4>{service.nom}</h4>
                <p>Voir les annonces</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section des annonces avec ref pour le défilement */}
      <div ref={annoncesSectionRef}>
        {(selectedSubService || searchTerm || filters.ville || filters.profil || filters.type) && (
          <div className="annonces-section">
            <div className="annonces-header">
              <h3>
                {selectedSubService 
                  ? `Annonces pour ${selectedSubService.nom}` 
                  : searchTerm 
                  ? `Résultats pour "${searchTerm}"`
                  : 'Annonces filtrées'}
              </h3>
              
              {/* Indicateurs de filtres actifs */}
              <div className="active-filters">
                {filters.ville && (
                  <span className="filter-badge">
                    Ville: {filters.ville}
                  </span>
                )}
                {filters.profil && (
                  <span className="filter-badge">
                    Profil: {filters.profil}
                  </span>
                )}
                {filters.type && (
                  <span className="filter-badge">
                    Type: {filters.type}
                  </span>
                )}
                {(filters.ville || filters.profil || filters.type) && (
                  <button onClick={clearFilters} className="clear-filters-btn">
                    Effacer les filtres
                  </button>
                )}
              </div>
            </div>
            
            {loading.annonces ? (
              <div className="loading">Chargement des annonces...</div>
            ) : annonces.length > 0 ? (
              <div className="annonces-gridcategorie">
                {annonces.map((annonce) => {
                  const imageUrl = annonce.imageUrl || getImageUrl(annonce);
                  
                  return (
                    <div 
                      key={annonce.id} 
                      className="annonce-cardcategorie"
                      onClick={() => handleAnnonceClick(annonce.id)}
                    >
                      <div className="annonce-image-containercategorie">
                        <img 
                          src={imageUrl} 
                          alt={annonce.titre}
                          onError={(e) => {
                            e.target.src = '/default-annonce.jpg';
                          }}
                        />
                      </div>
                      <div className="annonce-detailscategorie">
                        <h4>{annonce.titre}</h4>
                        <p className="annonce-descriptioncategorie">
                          {annonce.description ? `${annonce.description.substring(0, 100)}...` : 'Aucune description'}
                        </p>
                        <div className="annonce-metacategorie">
                          <span className="annonce-villecategorie">
                            <FiMapPin /> {annonce.ville}
                          </span>
                          <span className="annonce-prixcategorie">{annonce.prix} €</span>
                          <span className={`annonce-typecategorie ${annonce.type_annonce}`}>
                            {annonce.type_annonce}
                          </span>
                        </div>
                        {annonce.user && (
                          <div className="annonce-profilcategorie">
                            {/* <small>Profil: {annonce.user.profil || 'Non spécifié'}</small> */}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-annonces">
                <p>Aucune annonce ne correspond à votre recherche.</p>
                <button onClick={clearFilters} className="clear-filters-btn">
                  Effacer les filtres et réessayer
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Categorie;